import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  Stack,
  Paper,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import {
  Work,
  School,
  BusinessCenter,
  Code,
  Analytics,
  Engineering,
  Laptop,
  Storage,
} from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';

const Experience = () => {
  const { darkMode } = useThemeMode();

  const experiences = [
    {
      type: 'work',
      title: 'MERN Stack Developer',
      company: 'EnergiSpeak Analytics',
      period: 'Aug 2024 - Present',
      status: 'Current',
      icon: <Analytics />,
      description: [
        'Design and develop user-friendly dashboards using React.js and MUI',
        'Build and optimize scalable web applications with the MERN stack',
        'Create interactive charts and real-time analytics using React Chart.js 2',
        'Manage and optimize MongoDB databases for performance',
        'Integrate Redux for state management and data flow',
        'Develop RESTful APIs for efficient front-end and back-end communication'
      ],
      technologies: ['React.js', 'Node.js', 'MongoDB', 'Express.js', 'Redux', 'MUI', 'Chart.js', 'RESTful APIs'],
      color: '#4caf50',
    },
    {
      type: 'work',
      title: 'Junior Software Developer',
      company: 'Acumen Cognitive Engineering',
      period: 'Oct 2023 - Aug 2024',
      status: 'Previous',
      icon: <Engineering />,
      description: [
        'Developed software solutions based on client requirements',
        'Built and optimized web applications using React.js, Node.js, and Express.js',
        'Worked with PostgreSQL to optimize database performance',
        'Collaborated with cross-functional teams to enhance application functionality',
        'Contributed to feature enhancements and ensured high-quality code delivery'
      ],
      technologies: ['React.js', 'Node.js', 'Express.js', 'PostgreSQL', 'JavaScript', 'Team Collaboration'],
      color: '#2196f3',
    },
    {
      type: 'work',
      title: 'Intern',
      company: 'BridgeLabz Solutions',
      period: 'Mar 2022 - Sep 2022',
      status: 'Completed',
      icon: <Code />,
      description: [
        'Developed and deployed Employee Payroll and Address Book applications',
        'Built backend APIs using Spring Boot for efficient data management',
        'Designed responsive front-end interfaces with React.js and Material-UI',
        'Focused on creating intuitive and user-friendly UIs',
        'Improved application performance and followed best coding practices'
      ],
      technologies: ['React.js', 'Spring Boot', 'Material-UI', 'Java', 'API Development', 'UI/UX'],
      color: '#ff9800',
    },
    {
      type: 'education',
      title: 'BSc IT',
      company: 'Arunodaya University',
      period: '2024',
      status: 'Graduated',
      icon: <School />,
      description: [
        'Bachelor of Science in Information Technology',
        'Comprehensive study of software development, databases, and web technologies',
        'Strong foundation in programming languages and system design'
      ],
      technologies: ['Software Development', 'Database Management', 'Web Technologies', 'Programming'],
      color: '#9c27b0',
    },
    {
      type: 'education',
      title: 'Diploma',
      company: 'Bharati Vidyapeeth Institute Of Technology (Polytechnic)',
      period: '2020',
      status: 'Completed',
      icon: <Laptop />,
      description: [
        'Diploma in Computer Engineering/Technology',
        'Foundational knowledge in computer systems and programming',
        'Practical experience in software development and technical skills'
      ],
      technologies: ['Computer Engineering', 'Programming Fundamentals', 'Technical Skills'],
      color: '#607d8b',
    },
  ];

  return (
    <Box id="experience" sx={{
      py: 2,
      backgroundColor: darkMode
        ? 'rgba(220, 0, 78, 0.02)'
        : 'rgba(220, 0, 78, 0.01)'
    }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
            <Work sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '2.5rem' },
                fontWeight: 800,
                background: 'linear-gradient(45deg, #1976d2, #dc004e)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Experience & Education
            </Typography>
          </Box>


          {/* Horizontal Timeline Overview */}
          <Box sx={{
            maxWidth: '100%',
            mx: 'auto',
            mt: 4,
            mb: 6,
            position: 'relative',
            overflowX: 'auto',
            pb: 2,
            display: { xs: 'none', sm: 'block' }, // Hide on mobile for better UX
          }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: { xs: 2, sm: 4, md: 6 },
              minWidth: 'fit-content',
              px: { xs: 2, sm: 0 },
              position: 'relative',
            }}>
              {/* Horizontal connecting line */}
              <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '10%',
                right: '10%',
                height: 2,
                backgroundColor: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                transform: 'translateY(-50%)',
                zIndex: 0,
              }} />

              {[
                { year: 'Aug 2024 - Present', name: 'EnergiSpeak Analytics', type: 'MERN Stack Developer', color: '#4caf50', isCurrent: true },
                { year: '2024', name: 'BSc IT - Arunodaya University', type: 'Graduated', color: '#9c27b0' },
                {
                  year: 'Oct 2023 - Aug 2024', name: 'Acumen Cognitive Engineering', type: 'Junior Software Developer', color: '#2196f3'
                },
                { year: 'Mar 2022 - Sep 2022', name: 'BridgeLabz Solutions', type: 'Internship', color: '#ff9800' },
                { year: '2020', name: 'Diploma - Bharati Vidyapeeth', type: 'Diploma', color: '#607d8b' },
              ].map((item, index) => (
                <Box key={index} sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  position: 'relative',
                  zIndex: 1,
                  minWidth: { xs: 120, sm: 140, md: 160 },
                  textAlign: 'center',
                }}>
                  <Typography variant="body2" sx={{
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    mb: 1,
                    ...(item.isCurrent && {
                      animation: 'blink 2s infinite',
                      '@keyframes blink': {
                        '0%, 50%': { opacity: 1 },
                        '51%, 100%': { opacity: 0.3 },
                      },
                    }),
                  }}>
                    {item.year}
                  </Typography>

                  <Box sx={{
                    width: { xs: 14, sm: 16 },
                    height: { xs: 14, sm: 16 },
                    borderRadius: '50%',
                    backgroundColor: item.color,
                    border: `3px solid ${darkMode ? '#1a1a1a' : '#ffffff'}`,
                    boxShadow: `0 0 0 2px ${item.color}40`,
                    mb: 1,
                    zIndex: 2,
                    ...(item.isCurrent && {
                      animation: 'pulse 2s infinite',
                      '@keyframes pulse': {
                        '0%': {
                          boxShadow: `0 0 0 2px ${item.color}40`,
                          transform: 'scale(1)',
                        },
                        '50%': {
                          boxShadow: `0 0 0 6px ${item.color}20, 0 0 0 12px ${item.color}10`,
                          transform: 'scale(1.1)',
                        },
                        '100%': {
                          boxShadow: `0 0 0 2px ${item.color}40`,
                          transform: 'scale(1)',
                        },
                      },
                    }),
                  }} />

                  <Typography variant="body2" sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                    mb: 0.5,
                    lineHeight: 1.2,
                  }}>
                    {item.name}
                  </Typography>

                  <Chip
                    label={item.type}
                    size="small"
                    sx={{
                      backgroundColor: `${item.color}15`,
                      color: item.color,
                      fontSize: { xs: '0.6rem', sm: '0.65rem' },
                      height: { xs: 18, sm: 20 },
                      fontWeight: 500,
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Timeline
          position="alternate"
          sx={{
            px: { xs: 0, md: 2 },
            '& .MuiTimelineItem-root': {
              minHeight: 'auto',
            },
            '& .MuiTimelineContent-root': {
              px: { xs: 1, md: 2 },
              py: 0,
            },
            '& .MuiTimelineItem-root:before': {
              display: { xs: 'none', md: 'block' },
            },
          }}
        >
          {experiences.map((exp, index) => {
            const isLeft = index % 2 === 1; // Odd indices are on the left side

            return (
              <TimelineItem
                key={index}
                sx={{
                  '&:before': {
                    flex: { xs: 0, md: 1 },
                    padding: { xs: 0, md: '6px 16px' },
                  },
                }}
              >
                <TimelineSeparator>
                  <TimelineDot
                    sx={{
                      p: 0,
                      border: 'none',
                      boxShadow: 'none',
                      backgroundColor: 'transparent',
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: exp.color,
                        width: { xs: 40, md: 50 },
                        height: { xs: 40, md: 50 },
                        boxShadow: darkMode
                          ? `0 0 20px ${exp.color}40`
                          : `0 0 15px ${exp.color}30`,
                      }}
                    >
                      {exp.icon}
                    </Avatar>
                  </TimelineDot>
                  {index < experiences.length - 1 && (
                    <TimelineConnector
                      sx={{
                        bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                        width: 2,
                      }}
                    />
                  )}
                </TimelineSeparator>
                <TimelineContent
                  sx={{
                    pb: 4,
                    px: { xs: 2, md: 3 },
                    minWidth: 0,
                    flex: 1,
                    textAlign: { xs: 'left', md: isLeft ? 'right' : 'left' },
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 2, sm: 2.5, md: 3 },
                      background: darkMode
                        ? 'linear-gradient(135deg, rgba(26,26,26,0.8) 0%, rgba(42,42,42,0.8) 100%)'
                        : 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.9) 100%)',
                      border: `1px solid ${exp.color}30`,
                      borderRadius: 3,
                      position: 'relative',
                      transition: 'all 0.3s ease',
                      width: '100%',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      wordWrap: 'break-word',
                      textAlign: 'left', // Always left-align content inside the paper
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: darkMode
                          ? `0 10px 30px rgba(0,0,0,0.3), 0 0 20px ${exp.color}20`
                          : `0 10px 30px rgba(0,0,0,0.1), 0 0 15px ${exp.color}20`,
                        border: `1px solid ${exp.color}60`,
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 3,
                        background: `linear-gradient(90deg, ${exp.color}, ${exp.color}80)`,
                        borderRadius: '12px 12px 0 0',
                      },
                    }}
                  >
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: { xs: 'flex-start', md: 'center' },
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 1, sm: 0 },
                      mb: 2
                    }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            mb: 0.5,
                            color: exp.color,
                            fontSize: { xs: '1.1rem', md: '1.5rem' },
                            wordBreak: 'break-word',
                          }}
                        >
                          {exp.title}
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            mb: 1,
                            color: 'text.primary',
                            fontSize: { xs: '1rem', md: '1.25rem' },
                            wordBreak: 'break-word',
                          }}
                        >
                          {exp.company}
                        </Typography>
                      </Box>
                      <Chip
                        label={exp.status}
                        size="small"
                        sx={{
                          backgroundColor: `${exp.color}20`,
                          color: exp.color,
                          border: `1px solid ${exp.color}40`,
                          fontWeight: 600,
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: '0.7rem',
                          alignSelf: { xs: 'flex-start', sm: 'center' },
                          flexShrink: 0,
                        }}
                      />
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        mb: 3,
                        fontFamily: '"JetBrains Mono", monospace',
                        fontSize: '0.85rem',
                        fontWeight: 500,
                      }}
                    >
                      📅 {exp.period}
                    </Typography>

                    <Box sx={{ mb: 3 }}>
                      {exp.description.map((desc, descIndex) => (
                        <Box key={descIndex} sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          mb: 1.5,
                          width: '100%',
                        }}>
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              backgroundColor: exp.color,
                              mt: 0.75,
                              mr: { xs: 1.5, md: 2 },
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              lineHeight: 1.6,
                              color: 'text.primary',
                              fontSize: { xs: '0.85rem', md: '0.875rem' },
                              flex: 1,
                              wordBreak: 'break-word',
                              hyphens: 'auto',
                            }}
                          >
                            {desc}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <Divider sx={{ mb: 2, borderColor: `${exp.color}20` }} />

                    <Typography
                      variant="subtitle2"
                      sx={{
                        mb: 1.5,
                        fontWeight: 600,
                        color: 'text.secondary',
                        fontSize: '0.8rem',
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                      }}
                    >
                      Technologies & Skills
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                      {exp.technologies.map((tech, techIndex) => (
                        <Chip
                          key={techIndex}
                          label={tech}
                          size="small"
                          sx={{
                            backgroundColor: darkMode ? `${exp.color}15` : `${exp.color}10`,
                            color: exp.color,
                            border: `1px solid ${exp.color}30`,
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            '&:hover': {
                              backgroundColor: `${exp.color}25`,
                              transform: 'scale(1.05)',
                            },
                            transition: 'all 0.2s ease',
                          }}
                        />
                      ))}
                    </Stack>
                  </Paper>
                </TimelineContent>
              </TimelineItem>
            );
          })}
        </Timeline>
      </Container>
    </Box>
  );
};

export default Experience;